/**
 * EMAIL MAPPERS — conversione tra tipi UI e DTO REST.
 *
 * `direction` viaggia come stringa: nessuna tabella string<->int (yaml
 * `direction: string`). Niente status (vedi nota in emailTypes.ts).
 */

import { pickAuditFields } from '@crm/shared';
import type { Email, EmailCreatePayload, EmailUpdatePayload } from './emailTypes';

/**
 * Shape dell'Email come esce dal REST. `direction` resta string come
 * da schema.
 */
export interface EmailDto {
  emailId: number;
  companyId: number;
  groupId: number;
  userId: number;
  userName: string;
  createDate: string;
  modifiedDate: string;
  direction: string;
  emailDate: string;
  description: string;
}

/** DTO -> modello UI. */
export function emailFromDto(dto: EmailDto): Email {
  return {
    emailId: dto.emailId,
    ...pickAuditFields(dto),
    // direction: il backend valida contro {IN, OUT}; se arriva qualcosa
    // di diverso fallback su 'OUT' per non crashare la list.
    direction: (dto.direction === 'IN' || dto.direction === 'OUT') ? dto.direction : 'OUT',
    emailDate: dto.emailDate,
    description: dto.description,
  };
}

/** Modello UI -> DTO REST (per mock MSW). */
export function emailToDto(e: Email): EmailDto {
  return {
    emailId: e.emailId,
    ...pickAuditFields(e),
    direction: e.direction,
    emailDate: e.emailDate,
    description: e.description,
  };
}

/** Body POST/PATCH atteso dal backend. */
export interface EmailInput {
  direction?: string;
  emailDate?: string;
  description?: string;
}

/** Payload create UI -> DTO input. */
export function emailCreateToInput(p: EmailCreatePayload): EmailInput {
  return {
    direction: p.direction,
    emailDate: p.emailDate,
    description: p.description,
  };
}

/** Payload update UI -> DTO input parziale (PATCH). */
export function emailUpdateToInput(p: Omit<EmailUpdatePayload, 'emailId'>): EmailInput {
  const out: EmailInput = {};
  if (p.direction !== undefined) out.direction = p.direction;
  if (p.emailDate !== undefined) out.emailDate = p.emailDate;
  if (p.description !== undefined) out.description = p.description;
  return out;
}

/** Body PATCH -> Partial<Email> UI (per mock). */
export function emailInputFromDto(input: EmailInput): Partial<Email> {
  const out: Partial<Email> = {};
  if (input.direction !== undefined) {
    out.direction = input.direction === 'IN' ? 'IN' : 'OUT';
  }
  if (input.emailDate !== undefined) out.emailDate = input.emailDate;
  if (input.description !== undefined) out.description = input.description;
  return out;
}
